import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Service from '../services/ChannelYouTubeService';
import { formatEastern } from '../utils/easternTime';

const EMPTY_PROXY = { protocol: 'http', host: '', port: '', username: '' };

export default function ChannelYouTubeDialog({ channel, kind, onClose }) {
  const [connection, setConnection] = useState(null);
  const [proxy, setProxy] = useState(EMPTY_PROXY);
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [autoUpload, setAutoUpload] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dirty, setDirty] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const id = channel.id;

  const refresh = useCallback(async () => {
    const [next, jobs] = await Promise.all([
      Service.get(kind, id),
      Service.uploads(kind, id),
    ]);
    setConnection(next);
    setUploads(jobs);
    return next;
  }, [kind, id]);

  useEffect(() => {
    if (connection?.uploadBlockedReason) setAutoUpload(false);
  }, [connection]);

  useEffect(() => {
    let active = true;
    Promise.all([Service.get(kind, id, { refresh: true }), Service.uploads(kind, id)])
      .then(([next, jobs]) => {
        if (!active) return;
        setConnection(next);
        setProxy(next.proxy || EMPTY_PROXY);
        setAutoUpload(next.autoUpload && !next.uploadBlockedReason);
        setUploads(jobs);
      })
      .catch(() => {
        if (active) setError('Could not load channel connection.');
      });
    const interval = setInterval(() => {
      Promise.all([Service.get(kind, id), Service.uploads(kind, id)])
        .then(([next, jobs]) => {
          if (active) {
            setConnection(next);
            setUploads(jobs);
          }
        })
        .catch(() => {});
    }, 10000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [kind, id]);

  async function action(work) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await work();
    } catch (err) {
      setError(
        err.response?.data?.error || 'Could not complete this operation.',
      );
    } finally {
      setBusy(false);
    }
  }

  function changeProxy(key, value) {
    setProxy((current) => ({ ...current, [key]: value }));
    setDirty(true);
    setLink('');
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>YouTube · {channel.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}
          {!connection ? (
            <CircularProgress size={24} />
          ) : (
            <>
              {!connection.configured && (
                <Alert severity="warning">
                  YouTube OAuth credentials and callback URL must be configured
                  on the server before connecting.
                </Alert>
              )}
              <Alert
                severity={
                  connection.needsReconnect
                    ? 'warning'
                    : connection.connected
                      ? 'success'
                      : 'info'
                }
              >
                {connection.needsReconnect
                  ? 'Authorization expired or was revoked. Reconnect to resume uploads.'
                  : connection.connected
                    ? `Connected to ${connection.youtubeChannelTitle} (${connection.youtubeChannelId})`
                    : 'No YouTube channel connected.'}
              </Alert>
              {connection.connected && connection.uploadBlockedReason && (
                <Alert severity="warning">
                  {connection.uploadBlockedReason}{' '}
                  <Link href="https://studio.youtube.com/" target="_blank" rel="noopener noreferrer">
                    Open YouTube Studio
                  </Link>
                </Alert>
              )}
              <Typography variant="subtitle1">Channel proxy</Typography>
              <Typography variant="body2" color="text.secondary">
                All YouTube API requests use this proxy. A failed proxy pauses
                uploads.
              </Typography>
              <TextField
                select
                label="Proxy protocol"
                value={proxy.protocol}
                onChange={(e) => changeProxy('protocol', e.target.value)}
                disabled={busy}
              >
                <MenuItem value="http">HTTP</MenuItem>
                <MenuItem value="https">HTTPS</MenuItem>
              </TextField>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Proxy host"
                  sx={{ minWidth: 0 }}
                  value={proxy.host}
                  onChange={(e) => changeProxy('host', e.target.value)}
                  disabled={busy}
                  helperText="Public IPv4 address or hostname"
                />
                <TextField
                  label="Port"
                  type="number"
                  sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0 }}
                  value={proxy.port}
                  onChange={(e) => changeProxy('port', e.target.value)}
                  disabled={busy}
                  inputProps={{ min: 1, max: 65535 }}
                />
              </Stack>
              <TextField
                label="Proxy username"
                value={proxy.username}
                onChange={(e) => changeProxy('username', e.target.value)}
                disabled={busy}
                autoComplete="off"
              />
              <TextField
                label="Proxy password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setDirty(true);
                  setLink('');
                }}
                disabled={busy || clearPassword}
                autoComplete="new-password"
                placeholder={
                  connection.hasProxyPassword && !clearPassword
                    ? '••••••••'
                    : undefined
                }
                slotProps={{
                  inputLabel: {
                    shrink:
                      connection.hasProxyPassword && !clearPassword
                        ? true
                        : undefined,
                  },
                }}
                helperText={
                  connection.hasProxyPassword
                    ? 'Enter a new password to replace the saved one.'
                    : 'Optional for proxies without authentication.'
                }
              />
              {connection.hasProxyPassword && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={clearPassword}
                      disabled={busy}
                      onChange={(e) => {
                        setClearPassword(e.target.checked);
                        setDirty(true);
                        setLink('');
                      }}
                    />
                  }
                  label="Remove saved proxy password"
                />
              )}
              <Tooltip title={connection.uploadBlockedReason || ''}>
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autoUpload && !connection.uploadBlockedReason}
                        disabled={busy || Boolean(connection.uploadBlockedReason)}
                        onChange={(e) => {
                          setAutoUpload(e.target.checked);
                          setDirty(true);
                        }}
                      />
                    }
                    label="Automatically upload newly completed videos as private"
                  />
                </span>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                Uploads use the video title. Previously completed videos are not
                uploaded when you enable this setting.
              </Typography>
              <Button
                variant="contained"
                disabled={busy || !proxy.host || !proxy.port}
                onClick={() =>
                  action(async () => {
                    const next = await Service.save(kind, id, {
                      proxy,
                      autoUpload: autoUpload && !connection.uploadBlockedReason,
                      ...(clearPassword
                        ? { password: '' }
                        : password
                          ? { password }
                          : {}),
                    });
                    setConnection(next);
                    setAutoUpload(next.autoUpload && !next.uploadBlockedReason);
                    setPassword('');
                    setClearPassword(false);
                    setDirty(false);
                    setLink('');
                    setNotice('Channel settings saved.');
                  })
                }
              >
                Save settings
              </Button>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                sx={{
                  '& .MuiButton-root': {
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                  },
                }}
              >
                {!connection.connected || connection.needsReconnect ? (
                  <Button
                    disabled={
                      busy || dirty || !connection.proxy || !connection.configured
                    }
                    onClick={() =>
                      action(async () => {
                        const result = await Service.connect(kind, id);
                        setLink(result.url);
                      })
                    }
                  >
                    {connection.needsReconnect
                      ? 'Reconnect YouTube'
                      : 'Connect YouTube'}
                  </Button>
                ) : (
                  <Button
                    disabled={busy || dirty}
                    onClick={() =>
                      action(async () => {
                        let result;
                        try {
                          result = await Service.check(kind, id);
                        } catch (err) {
                          // A failed check can mark authorization as expired.
                          // Show Reconnect immediately while retaining the error.
                          await refresh().catch(() => {});
                          throw err;
                        }
                        setConnection(result.connection);
                        setUploads(await Service.uploads(kind, id));
                        setNotice(
                          'Connection checked. Upload eligibility and recent uploads updated.',
                        );
                      })
                    }
                  >
                    Check connection
                  </Button>
                )}
                {connection.connected && (
                  <>
                    <Button
                      id="youtube-more-button"
                      color="inherit"
                      disabled={busy}
                      aria-controls={moreAnchor ? 'youtube-more-menu' : undefined}
                      aria-haspopup="menu"
                      aria-expanded={Boolean(moreAnchor)}
                      endIcon={<MoreHorizIcon />}
                      onClick={(event) => setMoreAnchor(event.currentTarget)}
                      sx={{ color: 'text.secondary', flexShrink: 0 }}
                    >
                      More
                    </Button>
                    <Menu
                      id="youtube-more-menu"
                      anchorEl={moreAnchor}
                      open={Boolean(moreAnchor)}
                      onClose={() => setMoreAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      slotProps={{ list: { 'aria-labelledby': 'youtube-more-button' } }}
                    >
                      <MenuItem
                        disabled={busy}
                        sx={{ color: 'error.main' }}
                        onClick={() => {
                          setMoreAnchor(null);
                          action(async () => {
                            await Service.disconnect(kind, id);
                            setAutoUpload(false);
                            setLink('');
                            await refresh();
                            setNotice('Disconnected. Automatic uploads are disabled.');
                          });
                        }}
                      >
                        Disconnect YouTube
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Stack>
              {dirty && (
                <Typography variant="caption">
                  Save your changes before connecting or checking the connection.
                </Typography>
              )}
              {link && (
                <Alert severity="info">
                  Copy this link and open it in your browser configured with
                  this channel’s proxy. The login page uses your browser’s
                  network settings. The link expires in 10 minutes.
                  <TextField
                    fullWidth
                    multiline
                    label="Connection link"
                    value={link}
                    margin="normal"
                    slotProps={{ input: { readOnly: true } }}
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    onClick={() =>
                      action(async () => {
                        await navigator.clipboard.writeText(link);
                        setNotice('Connection link copied.');
                      })
                    }
                  >
                    Copy link
                  </Button>
                </Alert>
              )}
              <Typography variant="subtitle1">Recent uploads</Typography>
              {!uploads.length && (
                <Typography color="text.secondary" variant="body2">
                  New uploads will appear here after a video finishes
                  generating.
                </Typography>
              )}
              {uploads.map((upload) => (
                <Box
                  key={upload.id}
                  sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}
                >
                  <Typography variant="body2">{upload.title}</Typography>
                  <Typography variant="caption">
                    {upload.status === 'review'
                      ? 'Needs review in YouTube Studio'
                      : upload.status}{' '}
                    · {upload.progress}%
                  </Typography>
                  {['uploading', 'retrying', 'queued'].includes(
                    upload.status,
                  ) && (
                    <LinearProgress
                      variant="determinate"
                      value={upload.progress}
                      sx={{ my: 1 }}
                    />
                  )}
                  {upload.error && (
                    <Typography variant="body2" color="error">
                      {upload.error}
                    </Typography>
                  )}
                  {upload.publishAt && (
                    <Typography variant="caption" display="block">
                      Scheduled to publish {formatEastern(upload.publishAt)}
                    </Typography>
                  )}
                  {upload.thumbnailStatus !== 'none' && upload.thumbnailStatus && (
                    <Typography variant="caption" display="block">
                      {upload.thumbnailStatus === 'uploaded' ? 'Thumbnail uploaded' : 'Thumbnail pending'}
                    </Typography>
                  )}
                  {upload.youtubeVideoId && (
                    <Link
                      href={`https://www.youtube.com/watch?v=${upload.youtubeVideoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View private video
                    </Link>
                  )}
                  {upload.status === 'failed' && (
                    <Tooltip title={connection.uploadBlockedReason || ''}>
                      <span>
                        <Button
                          size="small"
                          disabled={busy || Boolean(connection.uploadBlockedReason)}
                          onClick={() =>
                            action(async () => {
                              await Service.retry(kind, id, upload.id);
                              await refresh();
                            })
                          }
                        >
                          Retry upload
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
